import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Heart, 
  Calendar, 
  MapPin, 
  Trash2,
  ChevronRight,
  Star,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = {
  district: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  regional: 'bg-green-500/10 text-green-500 border-green-500/20',
  state: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  national: 'bg-orange-500/10 text-orange-500 border-orange-500/20'
};

export const Favorites = () => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    if (user) {
      fetchFavorites();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`);
      setFavorites(response.data);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (tournamentId) => {
    setRemoving(tournamentId);
    try {
      await axios.delete(`${API}/favorites/${tournamentId}`);
      setFavorites(favorites.filter(f => f.tournament_id !== tournamentId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove from favorites');
    } finally {
      setRemoving(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="text-center py-12">
          <CardContent>
            <Heart className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">Login Required</h3>
            <p className="text-muted-foreground mb-6">
              Please login to view your favorite tournaments
            </p>
            <Link to="/login">
              <Button className="btn-primary">Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="favorites-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-black">
          <span className="text-primary">MY WATCHLIST</span>
        </h1>
        <p className="text-muted-foreground mt-1">
          Tournaments you're interested in
        </p>
      </div>

      {/* Stats */}
      <Card className="mb-8" data-testid="stats-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
              <Star className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-3xl font-black">{favorites.length}</p>
              <p className="text-sm text-muted-foreground">Saved Tournaments</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Favorites List */}
      {favorites.length > 0 ? (
        <div className="space-y-4">
          {favorites.map((favorite, index) => (
            <Card 
              key={favorite.id} 
              className="card-hover overflow-hidden"
              data-testid={`favorite-card-${index}`}
            >
              <CardContent className="p-0">
                <div className="flex items-center">
                  {/* Color Bar */}
                  <div 
                    className={`w-2 h-full min-h-[100px] ${
                      favorite.tournament_level === 'district' ? 'bg-blue-500' :
                      favorite.tournament_level === 'regional' ? 'bg-green-500' :
                      favorite.tournament_level === 'state' ? 'bg-purple-500' :
                      'bg-orange-500'
                    }`}
                  />
                  
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge 
                            variant="outline" 
                            className={LEVEL_COLORS[favorite.tournament_level] || LEVEL_COLORS.district}
                          >
                            {(favorite.tournament_level || 'district').toUpperCase()}
                          </Badge>
                        </div>
                        
                        <h3 className="font-bold text-lg mb-1">{favorite.tournament_name}</h3>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          {favorite.tournament_location && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {favorite.tournament_location}
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(favorite.start_date)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        <Link to={`/tournaments/${favorite.tournament_id}`}>
                          <Button variant="outline" size="sm" className="gap-1">
                            View <ChevronRight className="w-4 h-4" />
                          </Button>
                        </Link>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFavorite(favorite.tournament_id)}
                          disabled={removing === favorite.tournament_id}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          data-testid={`remove-favorite-${index}`}
                        >
                          {removing === favorite.tournament_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12" data-testid="no-favorites">
          <CardContent>
            <Heart className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">No Favorites Yet</h3>
            <p className="text-muted-foreground mb-6">
              Browse tournaments and save your favorites to track them here
            </p>
            <Link to="/discover">
              <Button className="btn-primary gap-2">
                Discover Tournaments
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
